import { Icon } from '@iconify/react'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatCurrency, formatNumber } from '@/lib/campaign-dashboard'
import { formatAttributionPageLabel } from '@/lib/campaign-page-url'
import {
  addUrlAttributionSearchTerms,
  applyChartVisibilityToDisplays,
  applyUrlAttributionView,
  formatAttributionPeriodLabel,
  hasUrlAttributionSearchDraft,
  isUrlAttributionRowActiveOnChart,
  resolveChartVisibilityHeaderState,
  resolveUrlAttributionSearchTerms,
  resolveUrlChartTooltipLabel,
  shouldClearUrlAttributionSearchDraft,
  summarizeUrlAttributionRows,
  type UrlAttributionConversionFilter,
  type UrlAttributionSortDirection,
  type UrlAttributionSortKey,
} from '@/lib/campaign-url-attribution-view'
import type { CampaignUrlAttribution } from '@/types/campaign-report'
import { ConversionPageLink } from '@/components/Tracking'
import { CampaignUrlAttributionToolbar } from './CampaignUrlAttributionToolbar'
import styles from './CampaignDashboard.module.css'

interface CampaignUrlAttributionPanelProps {
  attribution: CampaignUrlAttribution | null | undefined
  isLoading?: boolean
  error?: string | null
  onRetry?: () => void
  dateFrom?: string
  dateTo?: string
  draftDateFrom?: string
  draftDateTo?: string
  onDraftDateChange?: (field: 'dateFrom' | 'dateTo', value: string) => void
  onApplyPeriod?: () => void
}

function formatConversionRate(value: number): string {
  return `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`
}

function shortPageLabel(value: string): string {
  const trimmed = formatAttributionPageLabel(value)
  if (trimmed.length <= 42) {
    return trimmed
  }
  return `${trimmed.slice(0, 39)}...`
}

function SortIndicator({
  active,
  direction,
}: {
  active: boolean
  direction: UrlAttributionSortDirection
}) {
  if (!active) return <Icon icon="mdi:swap-vertical" />
  return <Icon icon={direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} />
}

function ChartVisibilityCheck({
  checked,
  indeterminate = false,
  ariaLabel,
  onCheckedChange,
}: {
  checked: boolean
  indeterminate?: boolean
  ariaLabel: string
  onCheckedChange: (checked: boolean) => void
}) {
  const icon = indeterminate
    ? 'mdi:minus-box'
    : checked
      ? 'mdi:checkbox-marked'
      : 'mdi:checkbox-blank-outline'
  const isMarked = checked || indeterminate

  return (
    <label className={styles.chartVisibilityCheck}>
      <input
        type="checkbox"
        className={styles.chartVisibilityCheckInput}
        checked={checked}
        aria-label={ariaLabel}
        ref={(element) => {
          if (element) {
            element.indeterminate = indeterminate
          }
        }}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <Icon
        icon={icon}
        className={isMarked ? styles.chartVisibilityCheckOn : styles.chartVisibilityCheckOff}
        aria-hidden
      />
    </label>
  )
}

export function CampaignUrlAttributionPanel({
  attribution,
  isLoading = false,
  error = null,
  onRetry,
  dateFrom = '',
  dateTo = '',
  draftDateFrom = '',
  draftDateTo = '',
  onDraftDateChange,
  onApplyPeriod,
}: CampaignUrlAttributionPanelProps) {
  const [searchDraft, setSearchDraft] = useState('')
  const [searchTerms, setSearchTerms] = useState<string[]>([])
  const [conversionFilter, setConversionFilter] =
    useState<UrlAttributionConversionFilter>('all')
  const [sortKey, setSortKey] = useState<UrlAttributionSortKey>('sales')
  const [sortDirection, setSortDirection] = useState<UrlAttributionSortDirection>('desc')
  const [hideChartUrls, setHideChartUrls] = useState(false)
  const [chartVisibilityOverrides, setChartVisibilityOverrides] = useState<Record<string, boolean>>(
    {}
  )

  const sourceRows = useMemo(() => attribution?.rows ?? [], [attribution])
  const activeSearchTerms = useMemo(
    () => resolveUrlAttributionSearchTerms(searchTerms, searchDraft),
    [searchDraft, searchTerms]
  )
  const visibleRows = useMemo(
    () =>
      applyUrlAttributionView(
        sourceRows,
        conversionFilter,
        activeSearchTerms,
        sortKey,
        sortDirection
      ),
    [activeSearchTerms, conversionFilter, sortDirection, sortKey, sourceRows]
  )
  const summary = useMemo(() => summarizeUrlAttributionRows(visibleRows), [visibleRows])
  const hasLocalFilters =
    searchTerms.length > 0 ||
    hasUrlAttributionSearchDraft(searchDraft) ||
    conversionFilter !== 'all'
  const canApplyPeriod = Boolean(
    onApplyPeriod &&
      draftDateFrom &&
      draftDateTo &&
      draftDateFrom <= draftDateTo
  )

  const chartRows = useMemo(
    () =>
      visibleRows
        .filter((row) =>
          isUrlAttributionRowActiveOnChart(row.page_url_display, chartVisibilityOverrides)
        )
        .map((row, index) => ({
          ...row,
          chartAxisLabel: hideChartUrls ? String(index + 1) : shortPageLabel(row.page_url_display),
        })),
    [chartVisibilityOverrides, hideChartUrls, visibleRows]
  )
  const visiblePageDisplays = useMemo(
    () => visibleRows.map((row) => row.page_url_display),
    [visibleRows]
  )
  const headerVisibilityState = useMemo(
    () => resolveChartVisibilityHeaderState(visiblePageDisplays, chartVisibilityOverrides),
    [chartVisibilityOverrides, visiblePageDisplays]
  )

  const setChartVisibility = (pageUrlDisplay: string, isActive: boolean) => {
    setChartVisibilityOverrides((current) => ({
      ...current,
      [pageUrlDisplay]: isActive,
    }))
  }

  const setAllVisibleChartVisibility = (isActive: boolean) => {
    setChartVisibilityOverrides((current) =>
      applyChartVisibilityToDisplays(current, visiblePageDisplays, isActive)
    )
  }

  const toggleSort = (key: UrlAttributionSortKey) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDirection(key === 'page' ? 'asc' : 'desc')
  }

  const addSearchTerms = () => {
    const nextTerms = addUrlAttributionSearchTerms(searchTerms, searchDraft)
    const shouldClearDraft = shouldClearUrlAttributionSearchDraft(searchTerms, searchDraft)
    setSearchTerms(nextTerms)
    if (shouldClearDraft) {
      setSearchDraft('')
    }
  }

  const removeSearchTerm = (term: string) => {
    setSearchTerms((current) => current.filter((currentTerm) => currentTerm !== term))
  }

  const clearLocalFilters = () => {
    setSearchDraft('')
    setSearchTerms([])
    setConversionFilter('all')
    setSortKey('sales')
    setSortDirection('desc')
  }

  const periodLabel =
    dateFrom && dateTo ? formatAttributionPeriodLabel(dateFrom, dateTo) : null

  return (
    <section className={styles.sectionCard} aria-labelledby="dashboard-url-attribution-title">
      <div>
        <h2 id="dashboard-url-attribution-title" className={styles.sectionTitle}>
          Páginas de origem
        </h2>
        <p className={styles.sectionSubtitle}>
          Visualizações únicas por sessão e vendas pagas agrupadas por página e UTM.
          {periodLabel ? ` Período ${periodLabel}.` : null}
          {attribution && attribution.total_origins > sourceRows.length
            ? ` Mostrando as ${sourceRows.length} principais de ${formatNumber(attribution.total_origins)} origens.`
            : null}
        </p>
      </div>

      {onDraftDateChange && onApplyPeriod ? (
        <CampaignUrlAttributionToolbar
          dateFrom={draftDateFrom}
          dateTo={draftDateTo}
          onDateFromChange={(value) => onDraftDateChange('dateFrom', value)}
          onDateToChange={(value) => onDraftDateChange('dateTo', value)}
          onApplyPeriod={onApplyPeriod}
          canApplyPeriod={canApplyPeriod}
          conversionFilter={conversionFilter}
          onConversionFilterChange={setConversionFilter}
          searchDraft={searchDraft}
          onSearchDraftChange={setSearchDraft}
          searchTerms={searchTerms}
          onAddSearchTerms={addSearchTerms}
          onRemoveSearchTerm={removeSearchTerm}
          hideChartUrls={hideChartUrls}
          onToggleHideChartUrls={() => setHideChartUrls((current) => !current)}
          onClearLocalFilters={clearLocalFilters}
          hasLocalFilters={hasLocalFilters}
        />
      ) : null}

      {isLoading ? (
        <div className={styles.skeletonBlock} aria-hidden="true" />
      ) : error ? (
        <div className={styles.chartState} role="alert">
          <p className={styles.stateText}>{error}</p>
          {onRetry ? (
            <button type="button" className={styles.actionButton} onClick={onRetry}>
              <Icon icon="mdi:refresh" />
              Recarregar dashboard
            </button>
          ) : null}
        </div>
      ) : sourceRows.length === 0 ? (
        <div className={styles.chartState}>
          <p className={styles.stateText}>
            Nenhuma visita ou venda com página identificada neste período.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.tableMeta}>
            <span className={styles.badgeInfo}>
              <Icon icon="mdi:web" />
              {formatNumber(summary.origins)} origens visíveis
            </span>
            <span className={styles.badgeInfo}>
              <Icon icon="mdi:eye-outline" />
              {formatNumber(summary.views)} visualizações
            </span>
            <span className={styles.badgeWarning}>
              <Icon icon="mdi:cash" />
              {formatNumber(summary.sales)} vendas · {formatCurrency(summary.revenue)}
            </span>
          </div>

          {visibleRows.length === 0 ? (
            <div className={styles.chartState}>
              <p className={styles.stateText}>
                Nenhuma origem corresponde aos filtros desta lista.
              </p>
            </div>
          ) : (
            <>
              {chartRows.length === 0 ? (
                <div className={styles.chartState}>
                  <p className={styles.stateText}>Nenhuma origem ativa no gráfico.</p>
                </div>
              ) : (
                <div className={styles.urlChartWrap}>
                  <ResponsiveContainer width="100%" height={Math.max(240, chartRows.length * 44)}>
                    <BarChart
                      data={chartRows}
                      layout="vertical"
                      margin={{ left: 8, right: 16, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.24)" />
                      <XAxis type="number" stroke="#64748b" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="chartAxisLabel"
                        width={hideChartUrls ? 48 : 180}
                        stroke="#64748b"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          const numericValue = typeof value === 'number' ? value : Number(value ?? 0)
                          return [formatNumber(numericValue), String(name ?? '')]
                        }}
                        labelFormatter={(value, payload) =>
                          resolveUrlChartTooltipLabel(value, payload)
                        }
                      />
                      <Legend />
                      <Bar dataKey="views" name="Visualizações" fill="rgba(94, 23, 235, 0.45)" radius={[0, 8, 8, 0]} />
                      <Bar dataKey="sales" name="Vendas" fill="rgba(212, 0, 107, 0.7)" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.chartVisibilityCol} scope="col">
                        <span className={styles.srOnly}>Gráfico</span>
                        <ChartVisibilityCheck
                          checked={headerVisibilityState === 'all'}
                          indeterminate={headerVisibilityState === 'some'}
                          ariaLabel={
                            headerVisibilityState === 'all'
                              ? 'Ocultar todas as origens do gráfico'
                              : 'Mostrar todas as origens no gráfico'
                          }
                          onCheckedChange={() =>
                            setAllVisibleChartVisibility(headerVisibilityState !== 'all')
                          }
                        />
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortableHeader}
                          onClick={() => toggleSort('page')}
                        >
                          Página
                          <SortIndicator active={sortKey === 'page'} direction={sortDirection} />
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortableHeader}
                          onClick={() => toggleSort('views')}
                        >
                          Visualizações
                          <SortIndicator active={sortKey === 'views'} direction={sortDirection} />
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortableHeader}
                          onClick={() => toggleSort('sales')}
                        >
                          Vendas
                          <SortIndicator active={sortKey === 'sales'} direction={sortDirection} />
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortableHeader}
                          onClick={() => toggleSort('conversion_rate')}
                        >
                          Conversão
                          <SortIndicator
                            active={sortKey === 'conversion_rate'}
                            direction={sortDirection}
                          />
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortableHeader}
                          onClick={() => toggleSort('revenue')}
                        >
                          Receita
                          <SortIndicator active={sortKey === 'revenue'} direction={sortDirection} />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => {
                      const isActiveOnChart = isUrlAttributionRowActiveOnChart(
                        row.page_url_display,
                        chartVisibilityOverrides
                      )

                      return (
                        <tr key={`${row.page_url_display}-${index}`}>
                          <td className={styles.chartVisibilityCol}>
                            <ChartVisibilityCheck
                              checked={isActiveOnChart}
                              ariaLabel={`Mostrar ${formatAttributionPageLabel(row.page_url_display)} no gráfico`}
                              onCheckedChange={(isActive) =>
                                setChartVisibility(row.page_url_display, isActive)
                              }
                            />
                          </td>
                          <td>
                            <ConversionPageLink display={row.page_url_display} />
                          </td>
                          <td>{formatNumber(row.views)}</td>
                          <td>{formatNumber(row.sales)}</td>
                          <td>{formatConversionRate(row.conversion_rate)}</td>
                          <td>{formatCurrency(row.revenue)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}
